/**
 * Wires the singleton WS client to the AuthContext lifecycle
 * (PRD-18 §FR-2.2).
 *
 *   <AuthProvider>
 *     <RealtimeProvider>
 *       <App />
 *     </RealtimeProvider>
 *   </AuthProvider>
 *
 * Behavior:
 *   - When `professor` flips non-null  → start() the client.
 *   - When `professor` flips back null → stop() the client.
 *   - Token is read fresh on every (re)connect via `getFreshAccessToken`,
 *     which also REFRESHES an expired JWT first: the handshake carries
 *     the token in the URL and an expired one 403s pre-upgrade, which
 *     the browser reports as a bare 1006 — without the proactive
 *     refresh, a tab waking from overnight sleep retried the same dead
 *     token on every backoff tick.
 *
 * The WS URL is derived from `VITE_API_BASE_URL` by swapping the
 * scheme to `ws://` / `wss://`. PRD-18 §FR-1.1.
 */

import { useAuth } from "@auth/AuthContext"
import { getFreshAccessToken } from "@auth/refreshAccess"
import { useEffect, type ReactNode } from "react"

import { realtimeClient } from "./client"

import { env } from "@/env"

function buildWsUrl(): string {
  const base = env.VITE_API_BASE_URL.replace(/\/+$/, "")
  const wsBase = base.replace(/^http:/, "ws:").replace(/^https:/, "wss:")
  return `${wsBase}/ws/`
}

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const { professor } = useAuth()
  const professorId = professor?.id ?? null

  useEffect(() => {
    if (!professorId) {
      realtimeClient.stop()
      return
    }
    realtimeClient.start(getFreshAccessToken, buildWsUrl)
    return () => {
      realtimeClient.stop()
    }
  }, [professorId])

  return <>{children}</>
}
